import { createLazyResource } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { RentalApplicationPaymentSettingRepository } from '~/repositories/settings/rentalApplicationPaymentSettingsRepository';
import type { RentalApplicationBillingSettings } from '~/swagger/settings/Api';
export const [RentalApplicationPaymentSettingsProvider, useRentalApplicationPaymentSettings] = createMagicDoorContext(
  'RentalApplicationPaymentSettings',
  () => {
    const repo = new RentalApplicationPaymentSettingRepository();
    const [rentalApplicationPaymentSettings, setRentalApplicationPaymentSettings] = createSignal<
      RentalApplicationBillingSettings | undefined
    >(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const [paymentSummaryResource, { refetch: refetchPaymentSummary }] = createLazyResource(() =>
      repo.getRentalApplicationPaymentSummary()
    );

    const getRentalApplicationPaymentSettings = async (): Promise<RentalApplicationBillingSettings | undefined> => {
      setIsLoading(true);

      try {
        const result = await repo.getRentalApplicationPaymentSettings();
        setRentalApplicationPaymentSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const updateRentalApplicationPaymentSettings = async (
      payload: RentalApplicationBillingSettings
    ): Promise<RentalApplicationBillingSettings | undefined> => {
      setIsLoading(true);

      try {
        const result = await repo.updateRentalApplicationPaymentSettings(payload);
        setRentalApplicationPaymentSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const deleteRentalApplicationPaymentSettings = async (): Promise<void> => {
      setIsLoading(true);

      try {
        await repo.deleteRentalApplicationPaymentSettings();
        setRentalApplicationPaymentSettings(undefined);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    return {
      isLoading,
      error,
      updateRentalApplicationPaymentSettings,
      getRentalApplicationPaymentSettings,
      deleteRentalApplicationPaymentSettings,
      rentalApplicationPaymentSettings,
      paymentSummaryResource,
      refetchPaymentSummary,
    };
  }
);
